// Data tools (Sources de données, Marques, API & outils) — formatting and the
// pure derivations the pages show. Backend timestamps are naive UTC strings:
// everything goes through parseUtc (KI-110) and renders in Paris time,
// French formats (kit § Rédaction). No React here: unit-tested on payloads
// shaped like the API (dataToolsModel.test.js).
import { formatRelative as sharedRelative, formatShortDate, formatShortDateTime, parseUtc } from '../utils/dates';

const nbsp = (text) => String(text).replace(/ /g, ' ');

export const formatCount = (value) => nbsp(Number(value || 0).toLocaleString('fr-FR'));

// 0.643 → « 64 % »
export const formatPercent = (ratio) => (ratio === null || ratio === undefined || Number.isNaN(ratio)
  ? '—'
  : nbsp(`${Math.round(ratio * 100)} %`));

// Dates: the shared short format (utils/dates — parseUtc, Paris time).
// « 28 oct. » this year, « 28 oct. 2025 » before.
export const formatShortDay = (value, now) => formatShortDate(value, undefined, { now, empty: '' });
export const formatDay = (value) => formatShortDate(value, undefined, { empty: '' });

// « 28 oct., 14:05 » — the year only when it is not this year.
export const formatStamp = (value, now) => formatShortDateTime(value, undefined, { now, empty: '' });

// « il y a 12 min », « il y a 3 h », « hier », then the day.
export const formatRelative = (value, now) => sharedRelative(value, undefined, now, { empty: '' });

export const formatMs = (ms) => {
  const value = Number(ms);
  if (!Number.isFinite(value)) return '';
  return value < 1000 ? nbsp(`${Math.round(value)} ms`) : nbsp(`${(value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} s`);
};

// The typed failure reasons of a source (connector_service._explain_reason),
// in the operator's words. `http_<code>` is an HTTP error of the system.
const REASONS = {
  auth_rejected: 'Connexion refusée : identifiant, mot de passe ou clé incorrect.',
  password_change_required: 'Le portail demande de changer le mot de passe du compte.',
  portal_unavailable: 'Le portail ne répond pas ou renvoie une page inutilisable.',
  unreachable: 'Système injoignable (adresse, réseau ou délai dépassé).',
  bad_payload: 'Réponse dans un format inattendu.',
  param_missing: 'Un paramètre requis (par client ou par marque) est absent.',
  config_error: 'Configuration incomplète (adresse ou parcours manquant).',
  not_found: 'Aucune commande ne correspond aux identifiants.',
  runner_unavailable: 'Le navigateur d’automatisation n’est pas disponible.',
  ok: 'Lecture réussie.',
};

export const reasonText = (reason, t) => {
  if (!reason) return '';
  const http = /^http_(\d+)$/.exec(reason);
  if (http) return t('dataToolsUi.reason.http', 'Le système a répondu par une erreur HTTP {{code}}.', { code: http[1] });
  return REASONS[reason] ? t(`dataToolsUi.reason.${reason}`, REASONS[reason]) : reason;
};

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

// The platform's own tracking (Ship24) and the Envoi du Net shipments come
// with the account: they are listed, never counted as « your » sources
// (ticket_journey_service.own_sources).
export const isOwnSource = (source) => source && source.kind !== 'platform' && source.key !== 'edn_shipments';

export const connectorsByKey = (connectors) => Object.fromEntries((connectors || []).filter((c) => c && c.key).map((c) => [c.key, c]));

// The 7-day figures of a source: the connector listing carries them for the
// spec sources (stats), the journey only reads / found.
export const sourceStats = (source, connector) => {
  const stats = connector?.stats;
  if (stats && stats.reads_7d !== undefined && stats.reads_7d !== null) {
    return {
      reads: stats.reads_7d,
      found: stats.found_7d ?? 0,
      failed: stats.failed_7d ?? null,
      lastSuccessAt: stats.last_success_at || null,
    };
  }
  if (source && source.reads_7d !== undefined && source.reads_7d !== null) {
    return { reads: source.reads_7d, found: source.found_7d ?? 0, failed: null, lastSuccessAt: null };
  }
  return null;
};

// Header and tiles of /connectors: counts over the account's own sources and
// the 7-day reads of the spec sources. Only fields the API returned.
export const sourcesSummary = (journey, connectors) => {
  const sources = (journey?.sources || []).filter(isOwnSource);
  const byKey = connectorsByKey(connectors);
  let reads = 0;
  let found = 0;
  let failed = 0;
  let measured = 0;
  sources.forEach((source) => {
    const stats = sourceStats(source, byKey[source.key]);
    if (!stats) return;
    measured += 1;
    reads += stats.reads || 0;
    found += stats.found || 0;
    failed += stats.failed || 0;
  });
  const failing = (connectors || []).filter((c) => c?.health?.status === 'failing');
  return {
    total: sources.length,
    atArrival: sources.filter((s) => (s.when || []).some((w) => String(w).startsWith('arrival'))).length,
    failing: failing.length,
    verifying: sources.filter((s) => s.state === 'verification').length,
    off: sources.filter((s) => s.state === 'off').length,
    measured,
    reads,
    found,
    failed,
    foundRate: reads ? found / reads : null,
  };
};

// A verification period (spec.verification: days + started_at) whose end
// has passed: the comparison is over, a person decides.
export const verificationEnd = (verification) => {
  const start = parseUtc(verification?.started_at);
  const days = Number(verification?.days);
  if (!start || !Number.isFinite(days) || days <= 0) return null;
  return new Date(start.getTime() + days * 86400000);
};

// « À vérifier » of /connectors: failing sources (with their reason and since
// when), comparisons that ended, then what the journey says needs a person
// (a required piece missing, a warning) — the journey's own « failing » and
// « verification » items are covered by the per-source rows above.
export const sourcesAttention = (journey, connectors, now = Date.now()) => {
  const items = [];
  (connectors || []).forEach((connector) => {
    if (connector?.health?.status === 'failing') {
      items.push({
        key: `failing:${connector.key}`, kind: 'failing', tone: 'danger', sourceKey: connector.key,
        title: connector.title || connector.key, reason: connector.health.last_reason,
        since: connector.health.first_failure_at, consecutive: connector.health.consecutive,
      });
    }
  });
  (connectors || []).forEach((connector) => {
    if (!connector?.shadow_of) return;
    const end = verificationEnd(connector.verification);
    if (end && end.getTime() <= now) {
      items.push({
        key: `verified:${connector.key}`, kind: 'verified', tone: 'warning', sourceKey: connector.key,
        title: connector.title || connector.key, shadowOf: connector.shadow_of, endedAt: end.toISOString(),
      });
    }
  });
  (journey?.steps || []).forEach((step) => {
    (step.items || []).forEach((item) => {
      if (item.key === 'failing' || item.key === 'verification') return;
      const blocking = item.state === 'missing' && item.level === 'required';
      if (!blocking && item.state !== 'warning') return;
      items.push({
        key: `journey:${step.key}:${item.key}`, kind: 'journey', tone: blocking ? 'danger' : 'warning',
        step: step.key, item,
      });
    });
  });
  const rank = { danger: 0, warning: 1 };
  return items.sort((a, b) => rank[a.tone] - rank[b.tone]);
};

// ---------------------------------------------------------------------------
// Brands
// ---------------------------------------------------------------------------

// Emails of a brand over both storage shapes (associated_emails, legacy
// associated_email).
export const brandEmails = (brand) => {
  if (brand?.associated_emails && brand.associated_emails.length > 0) return brand.associated_emails;
  if (brand?.associated_email) return [{ email: brand.associated_email, is_vip: false, legacy: true }];
  return [];
};

// KI-218: the spare-parts source only exists for a brand whose access key
// Unanimity configured (or that already has a URL).
export const hasPartsSource = (config) => !!(config && (config.parts_api_url || config.has_parts_api_key));

// The brand's tracking API: off (no block), disabled (Ship24 only), or on —
// with a missing key when it calls without one.
export const brandApiState = (brand) => {
  const config = brand?.tracking_api_config;
  if (!config) return 'none';
  if (config.enabled === false) return 'disabled';
  const configured = Boolean(config.orders_get_url || config.orders_tracking_url || config.stock_url);
  if (!configured) return 'none';
  return config.has_api_key ? 'ok' : 'missing_key';
};

export const agentsByBrand = (assistants) => {
  const map = {};
  (assistants || []).forEach((agent) => {
    if (!agent?.associated_brand_id) return;
    (map[agent.associated_brand_id] = map[agent.associated_brand_id] || []).push(agent);
  });
  return map;
};

export const brandsSummary = (brands, assistants) => {
  const list = brands || [];
  const emails = list.flatMap(brandEmails);
  const agents = agentsByBrand(assistants);
  return {
    total: list.length,
    mailboxes: emails.length,
    vip: emails.filter((e) => e.is_vip).length,
    withoutMailbox: list.filter((b) => brandEmails(b).length === 0).length,
    apiOn: list.filter((b) => ['ok', 'missing_key'].includes(brandApiState(b))).length,
    apiMissingKey: list.filter((b) => brandApiState(b) === 'missing_key').length,
    apiDisabled: list.filter((b) => brandApiState(b) === 'disabled').length,
    withSupportPage: list.filter((b) => b.support_page_url).length,
    withAgent: assistants ? list.filter((b) => (agents[b.id] || []).length > 0).length : null,
  };
};

// « À vérifier » of /brands: an API that calls without its key, a parts
// source whose access key is missing (the parts tools stay off).
export const brandsAttention = (brands) => {
  const items = [];
  (brands || []).forEach((brand) => {
    if (brandApiState(brand) === 'missing_key') {
      items.push({ key: `key:${brand.id}`, kind: 'missing_key', tone: 'danger', brand });
    }
    const config = brand.tracking_api_config;
    if (config && config.parts_api_url && !config.has_parts_api_key) {
      items.push({ key: `parts:${brand.id}`, kind: 'parts_key', tone: 'warning', brand });
    }
  });
  return items;
};

// ---------------------------------------------------------------------------
// OpenAPI tools (API & outils)
// ---------------------------------------------------------------------------

// An agent's tool id for an OpenAPI operation is « {schema_id}_{function_name} »
// (ai_assistant.py): the agents that use a tool are those holding one of its ids.
export const toolUsage = (schema, assistants) => {
  const ids = new Set((schema?.function_definitions || []).map((fn) => `${schema._id}_${fn.name}`));
  return (assistants || []).filter((agent) => (agent.selected_tool_ids || []).some((id) => ids.has(id)));
};

export const toolServers = (schema) => (schema?.schema_dict?.servers || [])
  .map((server) => (server && typeof server.url === 'string' ? server.url : null))
  .filter(Boolean);

export const toolsSummary = (schemas, assistants) => {
  const list = schemas || [];
  const operations = list.reduce((sum, schema) => sum + (schema.function_definitions || []).length, 0);
  const used = assistants ? list.filter((schema) => toolUsage(schema, assistants).length > 0).length : null;
  const agentIds = new Set();
  if (assistants) list.forEach((schema) => toolUsage(schema, assistants).forEach((agent) => agentIds.add(agent._id)));
  return { total: list.length, operations, used, agents: assistants ? agentIds.size : null };
};

// « À vérifier » of /openapi-schemes: a tool the agent cannot call (no
// operationId), a tool without a server address (every call fails).
export const toolsAttention = (schemas) => {
  const items = [];
  (schemas || []).forEach((schema) => {
    if (!(schema.function_definitions || []).length) items.push({ key: `ops:${schema._id}`, kind: 'no_operations', tone: 'danger', schema });
    else if (schema.schema_dict && !toolServers(schema).length) items.push({ key: `srv:${schema._id}`, kind: 'no_server', tone: 'danger', schema });
  });
  return items;
};

// The security schemes of a parsed OpenAPI document → the credential fields
// the tool stores (api_keys): « name » or « name_username » + « name_password ».
export const securitySchemesOf = (parsed) => Object.entries(parsed?.components?.securitySchemes || {})
  .map(([name, config]) => ({ name, type: config?.type, scheme: config?.scheme, in: config?.in }));

export const credentialFields = (schemes) => schemes.flatMap((scheme) => (scheme.type === 'http' && scheme.scheme === 'basic'
  ? [{ key: `${scheme.name}_username`, scheme, secret: false, part: 'username' }, { key: `${scheme.name}_password`, scheme, secret: true, part: 'password' }]
  : [{ key: scheme.name, scheme, secret: true, part: 'key' }]));
